import { useMemo } from 'react';
import { Text } from 'components';
import { format } from 'date-fns';
import { decimals } from 'domain/utils';
import { DATE_TIME_S_FORMAT } from 'domain/utils/time.utils';
import { useTranslation } from 'react-i18next';
import {
	ResponsiveContainer,
	ComposedChart,
	CartesianGrid,
	XAxis,
	YAxis,
	Tooltip,
	Legend,
	Line,
	Bar,
	Area,
} from 'recharts';
import { VariableColor } from 'domain/utils/varColor.utils';
import { Variable } from 'domain/models';
import { ConvertedData } from 'domain/models/data.models';
import { DefaultTFuncReturn } from 'i18next';
import { AxisConfigurationType } from './types';
import { Payload } from 'recharts/types/component/DefaultLegendContent';

export const ObservationGraphics = ({
	data,
	graphics,
	loading,
}: {
	data: ConvertedData[];
	graphics?: ('tp' | 'hvr' | 'watering')[];
	loading: boolean;
}) => {
	if (!graphics) {
		// All graphics
		graphics = ['tp', 'hvr'];
	}
	const noData = useMemo(
		() => !loading && (!data || data.length == 0),
		[loading, data]
	);
	const currentVars = useCurrentVars(data);

	const legendProps = useLegendProps(currentVars);

	return (
		<div className='relative w-full p-4'>
			<NoDataAnnouncement noData={noData} />
			{graphics.includes('hvr') && (
				<ResponsiveContainer
					className={noData ? 'select-none blur-[3px]' : 'm-4'}
					width='100%'
					height={200}>
					<ComposedChart
						width={300}
						height={260}
						data={data}
						margin={{ top: 5, bottom: 5 }}>
						<CartesianGrid strokeDasharray='3 3' />
						<XAxis
							scale='point'
							dataKey='ts'
							style={{ fontSize: '12px' }}
							padding={{ left: 35, right: 35 }}
							tickFormatter={(timeStr) =>
								format(new Date(timeStr), DATE_TIME_S_FORMAT)
							}
						/>
						<YAxis
							style={{ fontSize: '12px' }}
							dx={0}
							yAxisId='right'
							orientation='right'
							label={{
								value: 'HR / %',
								angle: -90,
								fontSize: 12,
							}}
						/>
						<YAxis
							style={{ fontSize: '12px' }}
							dx={0}
							yAxisId='right-wind'
							orientation='left'
							label={{
								value: 'Velocidad / km/h',
								angle: -90,
								fontSize: 12,
							}}
						/>
						<Tooltip content={<ObsTooltip />} />
						<Legend {...legendProps} />
						<Line
							isAnimationActive={false}
							dot={false}
							yAxisId='right'
							dataKey='hurs'
							stroke={VariableColor['hurs']}
							strokeDasharray='4 2'
							strokeWidth={3}
						/>
						<Area
							isAnimationActive={false}
							dot={false}
							yAxisId='right-wind'
							dataKey='wgust'
							fill={VariableColor['wgust']}
							stroke={VariableColor['wgust']}
						/>
						<Bar
							isAnimationActive={false}
							yAxisId='right-wind'
							dataKey='sfcwind'
							fill={VariableColor['sfcwind']}
							barSize={20}
						/>
					</ComposedChart>
				</ResponsiveContainer>
			)}

			{graphics.includes('tp') && (
				<ResponsiveContainer
					className={noData ? 'select-none blur-[3px]' : 'm-4'}
					width='100%'
					height={200}>
					<ComposedChart data={data} margin={{}}>
						<CartesianGrid strokeDasharray='3 3' />
						<XAxis
							scale='point'
							dataKey='ts'
							style={{ fontSize: '12px' }}
							padding={{ left: 35, right: 35 }}
							tickFormatter={(timeStr) =>
								format(new Date(timeStr), DATE_TIME_S_FORMAT)
							}
						/>
						<YAxis
							style={{ fontSize: '12px' }}
							dx={0}
							yAxisId='left'
							label={{
								value: 'Temperatura / ºC',
								angle: -90,
								fontSize: 12,
							}}
							domain={[0, 45]}
						/>
						<YAxis
							style={{ fontSize: '12px' }}
							dx={0}
							yAxisId='pr'
							orientation='right'
							label={{
								value: 'Precipitación / l/m2',
								angle: -90,
								fontSize: 12,
							}}
							domain={[0, 20]}
						/>

						<Tooltip content={<ObsTooltip />} />
						<Legend {...legendProps} />
						<Line
							dot={false}
							yAxisId='left'
							dataKey='tas'
							stroke={VariableColor['tas']}
							strokeWidth={1}
						/>
						<Bar
							yAxisId='pr'
							dataKey='pr'
							fill={VariableColor['pr']}
							stackId='pr'
						/>
						{graphics.includes('watering') && (
							<Bar
								yAxisId='pr'
								dataKey='watering'
								fill='#BBAAFF'
								stackId='pr'
							/>
						)}
					</ComposedChart>
				</ResponsiveContainer>
			)}
		</div>
	);
};

export const ObsTooltip = ({
	active,
	payload,
	label,
}: {
	active?: boolean;
	payload?: any[];
	label?: string;
}) => {
	const { t } = useTranslation('variables');

	if (active && payload && payload.length) {
		// Get the timestamp
		const dateAt = label
			? format(new Date(parseInt(label)), DATE_TIME_S_FORMAT)
			: '-';

		const variables = payload[0].payload;
		return (
			<div className='border-1 z-1000 border bg-white p-2 shadow'>
				<div className='py-2'>{`${dateAt}`}</div>
				<div className='desc text-sm'>
					{/* For each variable, show the key (translated) and 1 decimal value */}
					{Object.keys(variables)
						.filter((key) => key !== 'ts')
						.map((key) => (
							<div key={key} className='flex'>
								<span className='text-light w-24 truncate'>
									{t(`variables:${key}`)}:{' '}
								</span>
								<span className='w-10 pr-2 text-right'>
									{decimals(variables[key], 1)}
								</span>
								<span className='w-10 truncate pr-2 text-right'>
									{t(`variables:unit.${key}`)}
								</span>
							</div>
						))}
				</div>
			</div>
		);
	}

	return null;
};

const NoDataAnnouncement = ({ noData }: { noData: boolean }) => {
	const { t } = useTranslation(['labels']);

	// Show a [NO DATA] message if there is no data
	if (noData) {
		return (
			<div className='absolute z-10 h-full w-full'>
				<div className='flex h-full w-full items-center justify-center'>
					<Text
						className={
							'mb-5 text-2xl font-semibold shadow-gray-500 text-shadow-lg'
						}>
						{t('labels:no_data')}
					</Text>
				</div>
			</div>
		);
	}
	return null;
};

export const useAxisConfiguration = () => {
	const { t } = useTranslation('variables');

	const availableAxisConfiguration: AxisConfigurationType = {
		tas: {
			axis: 'left',
			domain: [0, 40],
			label: t('variables:tas'), // Temperature
			unit: t('variables:unit.tas'),
			color: VariableColor.tas,
		},
		pr: {
			type: 'bar',
			axis: 'left',
			domain: [0, 20],
			label: t('variables:pr'), // Pre
			unit: t('variables:unit.pr'),
			color: VariableColor.pr,
			legendIcon: 'square',
		},
		// HR
		hurs: {
			axis: 'right',
			domain: [0, 100],
			label: t('variables:hurs'), // HR
			unit: t('variables:unit.hurs'),
			color: VariableColor.hurs,
		},
		sfcwind: {
			axis: 'right',
			domain: [0, 60],
			label: t('variables:sfcwind'), // Wind
			unit: t('variables:unit.sfcwind'),
			color: VariableColor.sfcwind,
			legendIcon: 'square',
		},
		wgust: {
			axis: 'right',
			domain: [0, 60],
			label: t('variables:wgust'), // Gust
			unit: t('variables:unit.wgust'),
			color: VariableColor.wgust,
		},
		leafwetness: {
			axis: 'right',
			type: 'bar',
			domain: [0, 60],
			label: t('variables:leafwetness'), // Leaf Wetness
			unit: t('variables:unit.leafwetness'),
			color: VariableColor.leafwetness,
			legendIcon: 'square',
		},

		// pr_cumulative
		pr_cumulative: {
			type: 'area',
			hidden: true,
			color: '#756bb1',
			fill: VariableColor.pr,
			legendIcon: 'square',
		},
		tas_cumulative: {
			type: 'area',
			hidden: true,
			color: '#de2d26',
			fill: VariableColor.tas,
			legendIcon: 'square',
		},
		t_hour_cumulative: {
			type: 'area',
			hidden: true,
			color: '#DE7F31',
			fill: VariableColor.tas,
			legendIcon: 'square',
		},
	};

	return availableAxisConfiguration;
};

export const useCurrentVars = (data: ConvertedData[]) => {
	// Get the current variables
	const currentVars = useMemo(() => {
		return [
			...new Set(
				data
					.map((s) => {
						// remove keys without values
						return Object.keys(s).filter((key: any) => !!(s as any)[key]);
					})
					.flat()
			),
		];
	}, [data]);

	return currentVars;
};

export const useLegendProps = (vars?: string[]) => {
	const { t } = useTranslation('variables');

	const formatter = (key: string) => `${t(`variables:${key}`)}`;

	const axisConfiguration = useAxisConfiguration();

	const legendPayload: Array<Payload> = useMemo(() => {
		// Merge
		const payload = [];
		for (const key of Object.keys(axisConfiguration)) {
			if (vars && !vars.includes(key)) {
				continue;
			}
			const axis = axisConfiguration[key];
			payload.push({
				id: key,
				value: key,
				color: axis.color,
				type: axis.legendIcon ?? 'line',
			});
		}

		return payload;
	}, [axisConfiguration]);

	return {
		wrapperStyle: { fontSize: '14px' },
		iconType: 'plainline' as const,
		payload: legendPayload,
		formatter,
	};
};
